/**
 * features/invoices/InvoicesManager.tsx
 * ------------------------------------------------------------------
 * Phase 2 — Invoices list. Filter by status, search by number/client,
 * open a draft to edit or an issued invoice to view/void.
 * ------------------------------------------------------------------
 */
import { useMemo, useState } from 'react';
import { FileText, Plus, Search, Download, Mail } from 'lucide-react';
import { useInvoices } from './useInvoices';
import { InvoiceEditor } from './InvoiceEditor';
import { SendInvoiceModal } from './SendInvoiceModal';
import { downloadInvoicePdf } from '@/lib/invoicePdf';
import { useSettings } from '@/contexts/SettingsContext';
import { formatPula } from '@/lib/money';
import { DEFAULT_COMPANY_SETTINGS, type Invoice, type InvoiceStatus } from '@/types';
import { Button, Card, Input, Badge, Spinner } from '@/components/ui/primitives';

const STATUS_TONE: Record<InvoiceStatus, 'neutral' | 'brand' | 'success' | 'warning' | 'danger'> = {
  draft: 'neutral',
  issued: 'brand',
  part_paid: 'warning',
  paid: 'success',
  void: 'danger',
};

const FILTERS: Array<{ key: 'all' | InvoiceStatus; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Drafts' },
  { key: 'issued', label: 'Issued' },
  { key: 'part_paid', label: 'Part-paid' },
  { key: 'paid', label: 'Paid' },
  { key: 'void', label: 'Void' },
];

function fmtDate(iso: string): string {
  return iso ? new Date(iso).toLocaleDateString('en-BW', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
}

export function InvoicesManager() {
  const { invoices, loading } = useInvoices();
  const { settings } = useSettings();
  const company = settings ?? DEFAULT_COMPANY_SETTINGS;
  const [filter, setFilter] = useState<'all' | InvoiceStatus>('all');
  const [search, setSearch] = useState('');
  const [editor, setEditor] = useState<Invoice | null | 'new'>(null);
  const [sendInv, setSendInv] = useState<Invoice | null>(null);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return invoices
      .filter((i) => (filter === 'all' ? true : i.status === filter))
      .filter(
        (i) =>
          !q ||
          (i.number ?? '').toLowerCase().includes(q) ||
          i.client.name.toLowerCase().includes(q),
      );
  }, [invoices, filter, search]);

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by number or client…"
            className="pl-9"
          />
        </div>
        <Button icon={<Plus size={16} />} onClick={() => setEditor('new')}>
          New invoice
        </Button>
      </div>

      {/* Status filter */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`rounded-full px-3 py-1 text-sm font-semibold transition ${
              filter === f.key
                ? 'bg-brand text-white'
                : 'bg-black/5 text-primaryLight hover:bg-black/10 dark:bg-white/5 dark:text-gray-300'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <Spinner label="Loading invoices…" />
      ) : visible.length === 0 ? (
        <Card className="py-16 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-brandSoft text-brand">
            <FileText size={22} />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {search || filter !== 'all' ? 'No invoices match.' : 'No invoices yet — create your first.'}
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/5 text-left text-xs uppercase tracking-wide text-primaryLight dark:border-white/10 dark:text-gray-400">
                <th className="px-4 py-3 font-semibold">Number</th>
                <th className="px-4 py-3 font-semibold">Client</th>
                <th className="px-4 py-3 font-semibold">Issued</th>
                <th className="px-4 py-3 font-semibold">Due</th>
                <th className="px-4 py-3 text-right font-semibold">Total</th>
                <th className="px-4 py-3 text-right font-semibold">Balance</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((inv) => (
                <tr
                  key={inv.id}
                  onClick={() => setEditor(inv)}
                  className="cursor-pointer border-b border-black/5 transition last:border-0 hover:bg-brandSoft/40 dark:border-white/5 dark:hover:bg-white/5"
                >
                  <td className="px-4 py-3 font-semibold text-primary dark:text-gray-100">
                    {inv.number ?? <span className="text-primaryLight">Draft</span>}
                  </td>
                  <td className="px-4 py-3 text-primary dark:text-gray-200">{inv.client.name}</td>
                  <td className="px-4 py-3 text-primaryLight dark:text-gray-400">{fmtDate(inv.issueDate)}</td>
                  <td className="px-4 py-3 text-primaryLight dark:text-gray-400">{fmtDate(inv.dueDate)}</td>
                  <td className="px-4 py-3 text-right text-primary dark:text-gray-200">
                    {formatPula(inv.totals.total)}
                  </td>
                  <td className="px-4 py-3 text-right text-primary dark:text-gray-200">
                    {formatPula(inv.balance)}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={STATUS_TONE[inv.status]}>{inv.status.replace('_', ' ')}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    {inv.number && inv.status !== 'void' ? (
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => downloadInvoicePdf(inv, company)}
                          title="Download PDF"
                          className="rounded-lg p-1.5 text-primaryLight transition hover:bg-brandSoft hover:text-brand dark:text-gray-300 dark:hover:bg-white/5"
                        >
                          <Download size={16} />
                        </button>
                        <button
                          onClick={() => setSendInv(inv)}
                          title="Email invoice"
                          className="rounded-lg p-1.5 text-primaryLight transition hover:bg-brandSoft hover:text-brand dark:text-gray-300 dark:hover:bg-white/5"
                        >
                          <Mail size={16} />
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {editor !== null && (
        <InvoiceEditor invoice={editor === 'new' ? null : editor} onClose={() => setEditor(null)} />
      )}

      {sendInv && (
        <SendInvoiceModal invoice={sendInv} company={company} onClose={() => setSendInv(null)} />
      )}
    </div>
  );
}
